import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import React, { useContext, useEffect, useState } from 'react'
import API from '../../services/API'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import eventTypes from './../clients/eventTypes'
import DeleteIcon from '@mui/icons-material/Delete'
import AnDatePicker from '../../components/DateTime/DatePicker'
import { EventMetaContext, ShiftContext } from './SingleEvent/SingleEvent'
import { AddCircle } from '@mui/icons-material'
import BigModal from '../../components/Modals/BigModal'
import AddNewClient from '../clients/AddNewClient'
import EventAddressFields from './components/EventAddressFields'
import { eventAddressPayload } from '../../helpers/eventAddress'

const AddNewEvent = ({
  customCallbackFunction,
  orgName,
  eventMeta,
  opsPlanMeta,
  shifts,
}) => {
  const [eventTitle, setEventTitle] = useState(eventMeta?.title ?? '')
  const [what3Words, setWhat3Words] = useState(opsPlanMeta?.what3Words ?? '')
  const [startDate, setStartDate] = useState(
    eventMeta?.startDate ? moment(eventMeta.startDate) : null,
  )
  const [endDate, setEndDate] = useState(
    eventMeta?.endDate ? moment(eventMeta.endDate) : null,
  )
  // Until the user edits either end field, the end mirrors the start + 1h so
  // the form never opens into an invalid (midnight) end state.
  const [endTouched, setEndTouched] = useState(!!eventMeta?.endDate)
  const [eventType, setEventType] = useState(eventMeta?.eventType ?? '')
  const [organisations, setOrganisations] = useState([])
  const [selectedOrganisation, setSelectedOrganisation] = useState(null)
  const [eventAddress, setEventAddress] = useState(
    eventMeta?.eventAddress ?? null,
  )
  const [conflicts, setConflicts] = useState([])
  const filteredConflicts = conflicts.filter((x) =>
    eventMeta && eventMeta._id === x._id ? false : true,
  )
  const [loadingConflicts, setLoadingConflicts] = useState(false)
  const navigate = useNavigate()
  const [startDateError, setStartDateError] = useState(false)
  const [hasShiftApplicants, setHasShiftApplicants] = useState(false)

  const [createNewOrganisation, setCreateNewOrganisation] = useState(false)

  const blankFormErrors = {
    title: false,
    startDate: false,
    endDate: false,
  }
  const [formErrors, setFormErrors] = useState(blankFormErrors)

  const handleSubmit = () => {
    setFormErrors(blankFormErrors)
    // A half-typed picker value is a truthy but invalid moment, and
    // moment(invalid).toDate() would save the event with no end date at all.
    const startInvalid = !startDate || !moment(startDate).isValid()
    const endInvalid =
      !endDate ||
      !moment(endDate).isValid() ||
      moment(startDate).isAfter(moment(endDate))
    const errorExists = startInvalid || endInvalid || !eventTitle
    if (errorExists) {
      console.log('ERROR - NO START OR END DATE')
      if (startInvalid) setFormErrors((prev) => ({ ...prev, startDate: true }))
      if (endInvalid) setFormErrors((prev) => ({ ...prev, endDate: true }))
      if (!eventTitle) setFormErrors((prev) => ({ ...prev, title: true }))
      return
    } else if (eventMeta) {
      API.put('/edit-event', {
        eventID: eventMeta._id,
        startDate: moment(startDate).toDate(),
        endDate: moment(endDate).toDate(),
        title: eventTitle,
        organisation: selectedOrganisation,
        eventType: eventType,
        ...eventAddressPayload(eventAddress),
        'opsPlan.what3Words': what3Words,
      })
        .then((res) => {
          if (res.status === 200) {
            if (!customCallbackFunction) {
              window.location.reload()
            } else {
              customCallbackFunction(res.data._id)
            }
          }
        })
        .catch((e) => console.log(e))
    } else {
      API.post('/new-event', {
        startDate: moment(startDate).toDate(),
        endDate: moment(endDate).toDate(),
        title: eventTitle,
        organisation: selectedOrganisation,
        eventType: eventType,
        ...eventAddressPayload(eventAddress),
        'opsPlan.what3Words': what3Words,
      })
        .then((res) => {
          if (res.status === 200) {
            if (!customCallbackFunction) {
              navigate(`/events/${res.data.newID}`)
            } else {
              customCallbackFunction(res.data.newID)
            }
          }
        })
        .catch((e) => console.log(e))
    }
  }

  const handleDelete = () => {
    API.post('/delete-event', {
      eventID: eventMeta._id,
      shifts: shifts.reduce((accumulator, shift) => {
        accumulator.push(shift._id)
        return accumulator
      }, []),
    })
      .then((res) => res.status === 200 && navigate('/events/calendar'))
      .catch((e) => console.log(e))
  }

  useEffect(() => {
    if (eventMeta?.organisation) {
      setSelectedOrganisation({
        _id: eventMeta.organisation._id,
        orgName: eventMeta.organisation.orgName,
      })
      setEventType(eventMeta.eventType)
    }
  }, [])

  useEffect(() => {
    if (orgName) {
      setSelectedOrganisation(orgName)
    }
  }, [orgName])

  const checkConflicts = ({ start, end }) => {
    setLoadingConflicts(true)
    if (start && endDate) {
      API.post('/events', {
        start: moment(start ?? startDate).toDate(),
        end: moment(end ?? endDate).toDate(),
      })
        .then((res) => {
          setConflicts(res.data)
        })
        .catch((e) => console.log(e))
    }
    setLoadingConflicts(false)
  }

  const startDateInvalid = !!startDate && !moment(startDate).isValid()
  const endDateInvalid =
    !!endDate &&
    (!moment(endDate).isValid() || moment(startDate).isAfter(moment(endDate)))

  useEffect(() => {
    API.get('/event-org-options')
      .then((res) => {
        setOrganisations(res.data)
      })
      .catch((e) => console.log(e))
    if (eventMeta) {
      checkConflicts({
        start: eventMeta.startDate,
        end: eventMeta.endDate,
      })
    }
  }, [])

  useEffect(() => {
    if (eventMeta?._id) {
      API.get('/check-if-event-has-applicants/' + eventMeta._id).then((res) => {
        if (res.status === 200 && res.data === true) {
          setHasShiftApplicants(true)
        }
      })
    }
  }, [eventMeta])

  const isDifferentDay = !moment(startDate).isSame(endDate, 'day')

  return (
    <Grid
      container
      component="form"
      padding={1}
      spacing={1.5}
      // onSubmit={handleSubmit}
    >
      <Grid item xs={12}>
        <Typography variant="title2">
          {eventMeta ? `Edit ${eventMeta.title}` : 'Add a New Event'}
        </Typography>
      </Grid>
      <Grid item xs={12} lg={8}>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <TextField
              value={eventTitle}
              onChange={(e) => setEventTitle(e.target.value)}
              label="Event Title"
              autoComplete="off"
              required
              fullWidth
            />
          </Grid>
          <LocalizationProvider
            dateAdapter={AdapterMoment}
            adapterLocale="en-gb"
          >
            <Grid item xs={12} sm={6} lg={3}>
              <AnDatePicker
                label="Start Date"
                openTo="day"
                slotProps={{ textField: { error: formErrors.startDate } }}
                value={startDate}
                fullWidth
                onChange={(newDate) => {
                  setStartDate(newDate)
                  checkConflicts({
                    start: moment(newDate).startOf('day'),
                    end: moment(newDate).endOf('day'),
                  })
                  if (!endTouched) {
                    setEndDate(newDate ? moment(newDate).add(1, 'hour') : null)
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <FormControl fullWidth>
                <TimePicker
                  label="Start Time"
                  value={startDate}
                  onChange={(newTime) => {
                    setStartDate(newTime)
                    if (!endTouched) {
                      setEndDate(
                        newTime ? moment(newTime).add(1, 'hour') : null,
                      )
                    }
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <AnDatePicker
                label="End Date"
                openTo="day"
                slotProps={{ textField: { error: formErrors.endDate } }}
                value={endDate}
                fullWidth
                minDate={startDate ? moment(startDate) : undefined}
                disabled={!startDate}
                onChange={(newDate) => {
                  setEndTouched(true)
                  setEndDate(newDate ? moment(newDate) : null)
                  checkConflicts({
                    start: startDate,
                    end: newDate,
                  })
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <FormControl fullWidth>
                <TimePicker
                  label="End Time"
                  value={endDate}
                  disabled={!startDate}
                  minTime={
                    !isDifferentDay && startDate ? moment(startDate) : undefined
                  }
                  onChange={(newTime) => {
                    setEndTouched(true)
                    setEndDate(newTime)
                  }}
                />
              </FormControl>
            </Grid>
          </LocalizationProvider>
          <Grid item xs={12} lg={6}>
            <FormControl fullWidth required>
              <Autocomplete
                options={Object.keys(eventTypes).sort()}
                disablePortal
                id="eventType-select"
                onChange={(e, value) => setEventType(value)}
                defaultValue={eventMeta && eventMeta.eventType}
                renderInput={(params) => (
                  <TextField {...params} label="Event Type" required />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} lg={6}>
            <FormControl fullWidth required>
              <Autocomplete
                options={organisations}
                disablePortal
                disabled={!!orgName}
                id="organisation-select"
                getOptionLabel={(option) => option.orgName}
                isOptionEqualToValue={(option, value) =>
                  option?._id === value?._id
                }
                value={selectedOrganisation}
                onChange={(e, value) => setSelectedOrganisation(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Organisation"
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}

                          <InputAdornment position="end">
                            {!selectedOrganisation && (
                              <Tooltip title="Add a New Client">
                                <IconButton
                                  onClick={() => {
                                    setSelectedOrganisation(null)
                                    setCreateNewOrganisation(true)
                                  }}
                                >
                                  <AddCircle color="anGreen" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </InputAdornment>
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText={
                  <Alert severity="error">
                    <AlertTitle>No Organisation Found</AlertTitle>
                    <Button
                      onClick={() => setCreateNewOrganisation(true)}
                      variant="contained"
                      size="small"
                      fullWidth
                    >
                      Add New
                    </Button>
                  </Alert>
                }
              />
              <BigModal
                open={createNewOrganisation}
                onClose={() => setCreateNewOrganisation(false)}
              >
                <AddNewClient
                  customCallbackAction={(newClient) => {
                    setCreateNewOrganisation(false)
                    setOrganisations((prev) => [...prev, newClient])
                    setSelectedOrganisation(newClient)
                  }}
                  enforceType="Events"
                />
              </BigModal>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="title3">Event Location</Typography>
          </Grid>
          <Grid item xs={12}>
            <EventAddressFields
              value={eventAddress}
              onChange={setEventAddress}
              legacyLocation={
                eventMeta?.eventAddress ? undefined : eventMeta?.eventLocation
              }
              size="medium"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              value={what3Words}
              onChange={(e) => setWhat3Words(e.target.value)}
              label="What3Words"
              fullWidth
              autoComplete="off"
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              disabled={
                endDateInvalid ||
                startDateInvalid ||
                !endDate ||
                formErrors.startDate ||
                !startDate
              }
              onClick={() => handleSubmit()}
            >
              Submit
            </Button>
            {endDateInvalid && (
              <Alert severity="error">
                The End Date must be a valid date on or after the Start Date
              </Alert>
            )}
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} lg={4}>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          height="100%"
        >
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            padding={2}
            maxHeight="100%"
            overflow="auto"
            borderRadius={1}
            border="solid 1px rgba(0, 0, 0, 0.23)"
          >
            <Typography variant="title3">Conflicting Events:</Typography>
            {loadingConflicts && <LinearProgress />}
            {filteredConflicts.length === 0 && !loadingConflicts && (
              <Alert severity="success">No conflicts</Alert>
            )}
            {filteredConflicts.map((conflict) => (
              <Alert key={conflict._id} severity="error">
                <AlertTitle>{conflict.title}</AlertTitle>
                {moment(conflict.startDate).format('Do MMM YYYY')}-
                {moment(conflict.endDate).format('Do MMM YYYY')}
              </Alert>
            ))}
          </Box>
          <Box display="flex" justifyContent="flex-end">
            {!shifts?.some((x) => x.assignedTo) && !hasShiftApplicants && (
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
              >
                Delete Event
              </Button>
            )}
          </Box>
        </Box>
      </Grid>
    </Grid>
  )
}

export default AddNewEvent
